import dayjs, { Dayjs } from "dayjs";

import { APP_API } from "@/src/const";
import { GoogleCalendar } from "@/src/type/strapi/collection";
import { HttpRequest } from "@/src/util";

export interface TimeSlot {
  date: Dayjs;
  times: Dayjs[];
}

export interface AttendeeInfo {
  firstName: string;
  lastName: string;
  email: string;
}

function getAvailableTimeSlots(calendarConfig?: GoogleCalendar, calendarEvents?: any[]): TimeSlot[] | undefined {
  if (!calendarConfig || !calendarEvents) {
    return undefined;
  }

  const event = calendarEvents.shift();
  let nextEvent = event
    ? {
        startDate: dayjs(event.startISODateTime).tz(event.timeZone),
        endDate: dayjs(event.endISODateTime).tz(event.timeZone),
      }
    : null;

  let startTime = dayjs()
    .tz(calendarConfig.timeZone)
    .add(calendarConfig.leadTimeOffset || 0, "hours")
    .add(1, "hours")
    .set("minute", 0)
    .set("seconds", 0)
    .set("milliseconds", 0);

  const today = dayjs().tz(calendarConfig.timeZone);

  if (startTime.hour() > calendarConfig.lastAvailableTourStartTime) {
    startTime = startTime.add(1, "day").set("hour", calendarConfig.firstAvailableTourStartTime);
  } else if (startTime.hour() < calendarConfig.firstAvailableTourStartTime) {
    startTime = startTime.set("hour", calendarConfig.firstAvailableTourStartTime);
  }

  const dayDifference = startTime.day() - today.day();

  const timeSlots = Array.from({ length: calendarConfig.numberOfTourDays - dayDifference }, (_, i) => {
    // Tour day
    const tourDate = startTime.add(i, "day");
    const day = tourDate;

    // Tour times
    const times: Dayjs[] = [];
    const firstTourDay = i === 0;
    let nextTourStartTime = firstTourDay ? startTime : tourDate.hour(calendarConfig.firstAvailableTourStartTime).minute(0).second(0).millisecond(0);

    // Create tour times within the provided timeframe ensuring they don't overlap with an existing calendar event.
    while (nextTourStartTime.valueOf() <= tourDate.set("hour", calendarConfig.lastAvailableTourStartTime).set("minute", 0).valueOf()) {
      const nextTourEndTime = nextTourStartTime.add(calendarConfig.eventDurationMins, "minute");

      let keepTimeSlot = true;
      if (nextEvent) {
        // If this tour time starts on or after the end of the event we're comparing, then check the next event.
        // Keep checking until we pass events older than this tour time.
        while (nextEvent && nextTourStartTime.valueOf() >= nextEvent.endDate.valueOf()) {
          const event = calendarEvents.shift();
          nextEvent = event
            ? {
                startDate: dayjs(event.startISODateTime).tz(event.timeZone),
                endDate: dayjs(event.endISODateTime).tz(event.timeZone),
              }
            : null;
        }

        // Compare tour time with event time.
        if (nextEvent) {
          const nextEventStartTime = nextEvent.startDate.valueOf();
          const tourStartsBefore = nextTourStartTime.valueOf() < nextEventStartTime;
          const tourEndsOnOrBefore = nextTourEndTime.valueOf() <= nextEventStartTime;
          keepTimeSlot = tourStartsBefore && tourEndsOnOrBefore;
        }
      }

      if (keepTimeSlot) {
        times.push(nextTourStartTime);
      }

      nextTourStartTime = nextTourEndTime;
    }

    return { date: day, times };
  });

  if ((timeSlots ?? []).length === 0) {
    return [];
  }
  const numberOfTourDays = calendarConfig.numberOfTourDays ?? 7;
  return timeSlots.length > numberOfTourDays ? timeSlots.slice(0, numberOfTourDays) : timeSlots;
}

function getAvailableTimeSlotsYardi(calendarEvents: any[], numberOfTourDays: number): TimeSlot[] {
  const timeSlots: TimeSlot[] = [];

  calendarEvents.forEach((event) => {
    const currSlot = dayjs(event.startISODateTime).tz("Etc/UTC");
    const lastTimeSlot = timeSlots[timeSlots.length - 1];

    if (!timeSlots.length || lastTimeSlot.date.day() !== currSlot.day()) {
      return timeSlots.push({ date: currSlot, times: [currSlot] });
    }

    lastTimeSlot.times.push(currSlot);
  });

  return timeSlots.slice(0, numberOfTourDays);
}

function getAttendeeInfo(formFields: Record<string, string>): AttendeeInfo {
  const formFieldsEntries = Object.entries(formFields);

  let firstName: string | undefined;
  let lastName: string | undefined;
  formFieldsEntries.forEach(([key, value]) => {
    // looks for a field starting with the word 'first', eg: first_name or firstName
    if (key.startsWith("first")) {
      firstName = value;
    } else if (key.startsWith("last")) {
      lastName = value;
    }
  });

  if (!firstName) {
    // if there is no form field starting with 'first', sets the leadFirstName to the value of the first field in the form
    firstName = formFieldsEntries[0][1];
  }

  if (!lastName) {
    // if there is no form field starting with 'last', sets the lastName to the value of the second field in the form
    lastName = formFieldsEntries[1][1];
  }

  return {
    email: formFields.email,
    firstName,
    lastName,
  };
}

async function createGoogleCalendarEvent(calendarConfig?: GoogleCalendar, attendeeInfo?: AttendeeInfo, selectedTourTime?: Dayjs) {
  if (!calendarConfig || !attendeeInfo || !selectedTourTime) {
    return;
  }

  const description = calendarConfig.eventDescription;
  const summary = `${calendarConfig.eventSummary} ${attendeeInfo.firstName} ${attendeeInfo.lastName}`;
  const attendees = (calendarConfig?.defaultAttendees ?? []).map((attendee) => ({ email: attendee.email }));
  const calendarEvent = {
    calendarId: calendarConfig.calendarId,
    startISODateTime: selectedTourTime.format(),
    endISODateTime: selectedTourTime.add(calendarConfig.eventDurationMins, "minute").format(),
    timeZone: calendarConfig.timeZone,
    summary,
    description,
    attendees: [...attendees, { email: attendeeInfo.email }],
  };

  return await HttpRequest.http({
    host: `${APP_API}/api/v1/scheduletour`,
    method: "PUT",
    payload: JSON.stringify({ calendarEvent }),
    expectedResponse: {
      json: true,
    },
  });
}

async function createYardiCalendarEvent(
  formFields: Record<string, any>,
  attendeeInfo: AttendeeInfo,
  selectedTourTime: Dayjs,
  calendarConfig: GoogleCalendar
) {
  const calendarEvent = {
    startISODateTime: selectedTourTime.format(),
    formData: {
      ...formFields,
      firstName: attendeeInfo.firstName,
      lastName: attendeeInfo.lastName,
    },
  };

  return await HttpRequest.http({
    host: `${APP_API}/api/v1/scheduletour`,
    method: "PUT",
    payload: JSON.stringify({
      integrationType: "yardi",
      calendarConfig: calendarConfig,
      calendarEvent,
    }),
    expectedResponse: {
      json: true,
    },
  });
}

async function getEvents(integrationType: "google" | "yardi", calendarConfig: GoogleCalendar) {
  return await HttpRequest.http({
    host: `${APP_API}/api/v1/scheduletour`,
    method: "POST",
    payload: JSON.stringify({
      integrationType: integrationType,
      calendarConfig: {
        calendarId: calendarConfig.calendarId,
        timeZone: calendarConfig.timeZone,
        firstAvailableTourStartTime: calendarConfig.firstAvailableTourStartTime,
        lastAvailableTourStartTime: calendarConfig.lastAvailableTourStartTime,
        eventDurationMins: calendarConfig.eventDurationMins,
        numberOfTourDays: calendarConfig.numberOfTourDays,
      },
    }),
    expectedResponse: {
      json: true,
    },
  });
}

export default {
  getAvailableTimeSlots,
  getAvailableTimeSlotsYardi,
  getAttendeeInfo,
  createGoogleCalendarEvent,
  createYardiCalendarEvent,
  getEvents,
};
